import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useReduxStore } from "../hooks/useReduxStore";
import { isUserSubscribed, getActiveUserSubscription } from "../helper/accessTypeHelper";
import {
  SparklesIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  FilmIcon,
  MusicalNoteIcon,
  BookOpenIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/solid";

export default function SubscriptionSection({ dark = true }) {
  const { getState } = useReduxStore();
  const { userData } = getState("AUTH");
  const navigate = useNavigate();

  const isSubscribed = isUserSubscribed(userData);
  const activePlan = getActiveUserSubscription(userData);

  return (
    <div className="w-full space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
            <span>VIP Membership</span>
            {isSubscribed && (
              <span className="text-xs bg-amber-500/20 text-amber-300 border border-amber-500/40 px-2.5 py-0.5 rounded-full font-extrabold uppercase tracking-wider">
                Active
              </span>
            )}
          </h3>
          <p className="text-zinc-400 text-sm mt-1">
            Manage your BAB Digital subscription, perks, and billing details.
          </p>
        </div>
      </div>

      {isSubscribed ? (
        /* Active Subscriber View */
        <div className="rounded-2xl bg-gradient-to-br from-[#18181b] via-[#141414] to-black border border-amber-500/30 p-6 md:p-8 shadow-2xl relative overflow-hidden">
          {/* Subtle gold glow */}
          <div className="absolute top-0 right-0 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>

          <div className="relative z-10 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-zinc-800 pb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                  <SparklesIcon className="w-7 h-7 text-amber-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-lg md:text-xl font-bold text-white">
                      {activePlan?.title || activePlan?.name || "BAB Premium VIP"}
                    </h4>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                      ACTIVE
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <p className="text-zinc-400 text-xs sm:text-sm">
                      {activePlan?.duration
                        ? `${activePlan.duration} ${
                            activePlan.duration_type
                              ? activePlan.duration_type.charAt(0).toUpperCase() +
                                activePlan.duration_type.slice(1) +
                                (Number(activePlan.duration) > 1 &&
                                !activePlan.duration_type.endsWith("s")
                                  ? "s"
                                  : "")
                              : "Days"
                          } of Full VIP Access`
                        : "Unlimited VIP Access"}
                    </p>
                    {activePlan?.expires_at && (
                      <span className="text-[11px] text-amber-400/90 font-medium bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                        Expires:{" "}
                        {new Date(
                          String(activePlan.expires_at).trim().replace(" ", "T")
                        ).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate("/Subscription")}
                className="px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold text-secondary hover:text-white bg-secondary/10 hover:bg-secondary border border-secondary/30 transition-all flex items-center gap-1.5 self-start sm:self-auto"
              >
                <span>Upgrade / Renew</span>
                <ArrowRightIcon className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Included Benefits */}
            <div>
              <h5 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">
                Active Membership Benefits
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-zinc-900/60 border border-zinc-800/80">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-white">
                      Unlimited Exclusive Content
                    </p>
                    <p className="text-xs text-zinc-400">
                      Stream subscriber-only movies, albums, and books.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-zinc-900/60 border border-zinc-800/80">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-white">
                      100% Ad-Free Viewing
                    </p>
                    <p className="text-xs text-zinc-400">
                      Zero commercial interruptions on all free content.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-zinc-900/60 border border-zinc-800/80">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-white">
                      Ultra HD & Lossless Audio
                    </p>
                    <p className="text-xs text-zinc-400">
                      Crisp playback on mobile, desktop, and tablet.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-zinc-900/60 border border-zinc-800/80">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-white">
                      Priority VIP Support
                    </p>
                    <p className="text-xs text-zinc-400">
                      Fast-track customer assistance whenever you need it.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Explore Link */}
            <div className="pt-2">
              <Link
                to="/ProductCollection"
                className="inline-flex items-center gap-2 text-sm font-bold text-amber-400 hover:text-amber-300 transition-colors"
              >
                <span>Browse Exclusive VIP Titles Now</span>
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      ) : (
        /* Non-Subscriber Upsell Card */
        <div className="rounded-2xl bg-gradient-to-br from-[#1c1c1f] via-[#141414] to-black border border-zinc-800 p-6 md:p-10 shadow-2xl relative overflow-hidden text-center sm:text-left">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="space-y-3 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-secondary/15 text-secondary border border-secondary/30">
                <SparklesIcon className="w-4 h-4" />
                <span>BAB VIP Membership</span>
              </div>
              <h4 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                Unlock Everything BAB Digital Has to Offer
              </h4>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Join thousands of subscribers enjoying unlimited access to
                exclusive releases, subscriber-only films, audio tracks, and an
                uninterrupted ad-free streaming experience.
              </p>
              <div className="flex flex-wrap gap-4 pt-2 text-xs text-zinc-300">
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-secondary" />
                  Subscribers-only content
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-secondary" />
                  100% Ad-Free
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-secondary" />
                  Cancel anytime
                </span>
              </div>
            </div>

            <div className="flex-shrink-0 w-full sm:w-auto">
              <Link
                to="/Subscription"
                className="block sm:inline-block w-full text-center px-8 py-4 rounded-xl text-sm font-bold bg-secondary hover:bg-orange-500 text-white shadow-xl shadow-secondary/25 transition-all hover:scale-[1.02] active:scale-95"
              >
                View Plans & Upgrade
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
